<template>
  <div
    ref="container"
    :aria-label="ariaLabel"
    class="tw:relative tw:items-stretch tw:gap-1 tw:rounded-full tw:bg-gray-100 tw:p-1 tw:flex tw:w-full"
    data-testid="base-button-group"
    role="radiogroup"
  >
    <!-- Animated background -->
    <div
      aria-hidden="true"
      class="tw:pointer-events-none tw:absolute tw:left-1 tw:top-1 tw:rounded-full tw:bg-white tw:shadow-sm tw:transition-transform tw:duration-200 tw:ease-in-out"
      :style="activeBackgroundStyle"
    />

    <template
      v-for="(item, index) in itemsFormatted"
      :key="item.value"
    >
      <button
        ref="activeElements"
        :aria-checked="item.active"
        :aria-disabled="item.disabled"
        class="tw:flex-1 tw:relative tw:z-10 tw:flex tw:items-center tw:justify-center tw:whitespace-nowrap tw:rounded-full tw:border-0 tw:bg-transparent tw:px-3 tw:py-1 tw:text-sm tw:transition-colors tw:duration-200 tw:ease-in-out"
        :class="[
          item.active
            ? 'tw:text-denim-800'
            : 'tw:text-denim-600 tw:hover:bg-gray-200',
          item.disabled
            ? 'tw:cursor-not-allowed tw:opacity-50'
            : item.active
              ? 'tw:cursor-default'
              : 'tw:hover:cursor-pointer',
        ]"
        :data-testid="`base-button-group-item-${String(item.value)}`"
        :disabled="item.disabled"
        role="radio"
        :tabindex="item.active && !item.disabled ? 0 : -1"
        type="button"
        @click="selectItem(index)"
        @keydown.left.prevent="
          handleKeyNavigation(NavigationDirection.LEFT, index)
        "
        @keydown.right.prevent="
          handleKeyNavigation(NavigationDirection.RIGHT, index)
        "
      >
        <BaseStreemTooltip
          v-if="item.disabled && item.disabledReason"
          :content-class="'tw:bg-denim-800 tw:text-white tw:p-2'"
          data-testid="base-button-group-item-tooltip"
        >
          {{ item.disabledReason }}
        </BaseStreemTooltip>
        <span data-testid="base-button-group-item-label">
          {{ item.label }}
        </span>
      </button>

      <!-- Divider -->
      <div
        v-if="index < itemsFormatted.length - 1"
        class="tw:relative tw:z-10 tw:flex tw:items-center tw:justify-center tw:text-denim-200 tw:text-xl"
        :class="{
          'tw:opacity-0': index === activeIndex - 1 || index === activeIndex,
        }"
        data-testid="base-button-group-divider"
      >
        |
      </div>
    </template>
  </div>
</template>

<script lang="ts">
export interface ItemOption<T> {
  active?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  label: string;
  value: T;
}
</script>

<script setup lang="ts" generic="T extends string | number | symbol | boolean">
import { computed, type CSSProperties, useTemplateRef } from "vue";

import BaseStreemTooltip from "shared/components/base/BaseStreemTooltip";

enum NavigationDirection {
  LEFT = "left",
  RIGHT = "right",
}

const props = defineProps<{
  ariaLabel?: string;
  items: ItemOption<T>[];
}>();

const modelValue = defineModel<T>();

const container = useTemplateRef<HTMLDivElement>("container");
const activeElements = useTemplateRef<HTMLElement[]>("activeElements");

const itemsFormatted = computed<ItemOption<T>[]>(() =>
  props.items.map((item) => ({
    ...item,
    active: item.value === modelValue.value,
  }))
);

const activeIndex = computed<number>(() =>
  itemsFormatted.value.findIndex((item) => item.active)
);

const activeBackgroundStyle = computed<CSSProperties>(() => {
  const elements = activeElements.value;
  const currentActiveIndex = activeIndex.value;

  if (
    !elements?.length ||
    currentActiveIndex === -1 ||
    !elements[currentActiveIndex]
  ) {
    return {
      opacity: 0,
      transform: "translateX(0)",
      width: 0,
      height: 0,
    };
  }

  const activeEl = elements[currentActiveIndex];
  const offsetLeft = activeEl.offsetLeft - 4; // Adjust for new padding (4px instead of 6px)

  return {
    opacity: 1,
    transform: `translateX(${Math.max(0, offsetLeft)}px)`,
    width: `${activeEl.offsetWidth}px`,
    height: `${activeEl.offsetHeight}px`,
  };
});

function selectItem(index: number): void {
  if (
    index < 0 ||
    index >= itemsFormatted.value.length ||
    itemsFormatted.value[index].disabled
  )
    return;

  modelValue.value = props.items[index].value;
  activeElements.value?.[index]?.focus();
}

function handleKeyNavigation(
  direction: NavigationDirection,
  currentIndex: number = 0
): void {
  const offset = direction === NavigationDirection.LEFT ? -1 : 1;
  let nextIndex = currentIndex + offset;

  while (
    nextIndex >= 0 &&
    nextIndex < props.items.length &&
    itemsFormatted.value[nextIndex].disabled
  ) {
    nextIndex += offset;
  }

  if (nextIndex >= 0 && nextIndex < props.items.length) {
    selectItem(nextIndex);
  }
}
</script>
